import { Injectable } from '@angular/core';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PreviewGateService {

  private readonly config = environment.previewGate;
  private readonly blockedSubject: BehaviorSubject<boolean>;
  /** Session only fallback used when localStorage is not available (private mode, blocked cookies, ...). */
  private memoryUnlocked = false;

  constructor() {
    this.memoryUnlocked = this.hasValidMarker();
    this.blockedSubject = new BehaviorSubject<boolean>(this.isBlocked());
  }

  /** Emits `true` as long as the application content has to stay hidden. */
  getIsBlockedState(): Observable<boolean> {
    return this.blockedSubject.asObservable();
  }

  /** The gate only runs where `environment.previewGate.enabled` is set. */
  isEnabled(): boolean {
    return this.config.enabled === true;
  }

  isUnlocked(): boolean {
    return this.memoryUnlocked || this.hasValidMarker();
  }

  isBlocked(): boolean {
    return this.isEnabled() && !this.isUnlocked();
  }

  /**
   * Verifies the given credentials and persists the unlock marker on success.
   * Emits `false` when the credentials do not match.
   */
  unlock(username: string, password: string): Observable<boolean> {
    const normalizedUsername = (username || '').trim().toLowerCase();
    const normalizedPassword = (password || '').trim();

    return from(this.hash(`${this.config.salt}:${normalizedUsername}:${normalizedPassword}`))
      .pipe(
        map(hash => !!this.config.credentialsHash && hash === this.config.credentialsHash),
        tap(unlocked => {
          if (!unlocked) {
            return;
          }

          // Always unlock the current session, even when the marker cannot be persisted.
          this.memoryUnlocked = true;
          this.writeMarker(this.config.credentialsHash);
          this.blockedSubject.next(this.isBlocked());
        })
      );
  }

  private hasValidMarker(): boolean {
    return !!this.config.credentialsHash && this.readMarker() === this.config.credentialsHash;
  }

  private async hash(value: string): Promise<string> {
    const subtle = this.getSubtleCrypto();

    if (!subtle) {
      throw new Error('Web Crypto API is not available, the preview gate cannot verify the credentials.');
    }

    const digest = await subtle.digest('SHA-256', new TextEncoder().encode(value));

    return Array.from(new Uint8Array(digest))
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  private getSubtleCrypto(): SubtleCrypto | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cryptoApi = (typeof window !== 'undefined' ? window.crypto : undefined) || (globalThis as any).crypto;

    return cryptoApi && cryptoApi.subtle ? cryptoApi.subtle : null;
  }

  private readMarker(): string | null {
    try {
      return localStorage.getItem(this.config.storageKey);
    } catch {
      return null;
    }
  }

  private writeMarker(marker: string): void {
    try {
      localStorage.setItem(this.config.storageKey, marker);
    } catch {
      // localStorage unavailable, the memory fallback keeps the current session unlocked.
    }
  }

}

